import type { Asset, Tag } from '../types'
import { MOCK_TAGS } from '../data/mockTags'

/**
 * Drop-in image library
 * -------------------------------------------------------------
 * Every image inside the project's `library/` folder is picked up
 * automatically — no code changes, no restart.
 *
 *   library/<Frame Name>/<file name>.jpg
 *
 * The subfolder name becomes a source-frame tag, the file name becomes the
 * title, and any known tag word in the file name is detected as a tag. This
 * deliberately mirrors the future Miro import, where a frame's name becomes a
 * tag on every image inside it.
 *
 * See library/HOW-TO.md for the user-facing version of these rules.
 */

const IMAGE_MODULES = import.meta.glob('/library/**/*.{png,jpg,jpeg,webp,avif,gif}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
}

/** Lower-case, punctuation-free form used for matching tag words. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

function slugify(value: string): string {
  return normalise(value).replace(/\s+/g, '-')
}

/** Known tag words, longest first so "rigid box" wins over "box". */
const TAG_MATCHERS = MOCK_TAGS.filter((tag) => tag.group !== 'source')
  .flatMap((tag) => [
    { id: tag.id, phrase: normalise(tag.name) },
    { id: tag.id, phrase: normalise(tag.id) },
  ])
  .filter((entry) => entry.phrase.length > 2)
  .sort((a, b) => b.phrase.length - a.phrase.length)

function detectTags(fileName: string): { tagIds: string[]; title: string } {
  let remaining = ` ${normalise(fileName)} `
  const tagIds: string[] = []

  for (const { id, phrase } of TAG_MATCHERS) {
    if (tagIds.includes(id)) continue
    const needle = ` ${phrase} `
    if (remaining.includes(needle)) {
      tagIds.push(id)
      remaining = remaining.replace(needle, ' ')
    }
  }

  const leftover = remaining.trim()
  const source = leftover.length > 0 ? leftover : normalise(fileName)
  const title = source.charAt(0).toUpperCase() + source.slice(1)
  return { tagIds, title }
}

interface ParsedFile {
  asset: Asset
  frameTag: Tag | null
}

function parse(path: string, url: string): ParsedFile {
  // "/library/2025 Beverage Audit/cold brew carton.jpg"
  const segments = path.split('/').filter(Boolean)
  const fileWithExtension = segments[segments.length - 1]
  const folder = segments.length > 2 ? segments[1] : null

  const dot = fileWithExtension.lastIndexOf('.')
  const fileName = dot > 0 ? fileWithExtension.slice(0, dot) : fileWithExtension
  const extension = dot > 0 ? fileWithExtension.slice(dot + 1).toLowerCase() : ''

  const { tagIds, title } = detectTags(fileName)

  let frameTag: Tag | null = null
  if (folder) {
    frameTag = { id: `frame-local-${slugify(folder)}`, name: folder, group: 'source' }
    tagIds.push(frameTag.id)
  }

  const asset: Asset = {
    id: `local-${slugify(path.replace('/library/', ''))}`,
    title,
    originalPath: url,
    thumbnailPath: url,
    source: 'upload',
    miroBoardId: null,
    sourceFrame: frameTag ? frameTag.id : null,
    fileHash: null,
    perceptualHash: null,
    // Real dimensions are unknown until the browser loads the file; the card
    // measures them on load and Miro keeps the image's own aspect ratio.
    width: 0,
    height: 0,
    mimeType: MIME_BY_EXTENSION[extension] ?? 'image/png',
    tagIds,
    aiDescription: null,
    createdAt: new Date().toISOString(),
  }

  return { asset, frameTag }
}

const parsed = Object.entries(IMAGE_MODULES)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([path, url]) => parse(path, url))

/** Images the user dropped into library/. Empty when the folder has none. */
export const LOCAL_ASSETS: Asset[] = parsed.map((entry) => entry.asset)

/** One source-frame tag per subfolder of library/. */
export const LOCAL_TAGS: Tag[] = Array.from(
  new Map(
    parsed
      .map((entry) => entry.frameTag)
      .filter((tag): tag is Tag => tag !== null)
      .map((tag) => [tag.id, tag]),
  ).values(),
)
